import React, { useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Plus,
  Trash2,
  Edit2,
  GripVertical,
  Image as ImageIcon,
  Upload,
  ExternalLink,
  CheckCircle2,
  Save,
  X
} from 'lucide-react';

// Mock data para los carruseles
const initialAliadosLogos = [
  { id: 1, name: 'Colegio Nacional de Inmobiliarios', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://cni.org.mx', active: true },
  { id: 2, name: 'AMPI', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://ampi.org', active: true },
  { id: 3, name: 'CONOCER', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://conocer.gob.mx', active: true },
  { id: 4, name: 'Mercado Libre', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://mercadolibre.com.mx', active: true },
  { id: 5, name: 'EasyBroker', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://easybroker.com', active: true },
  { id: 6, name: 'Morgana', logo: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop', url: 'https://morgana.mx', active: false },
];

const initialProveedoresLogos = [
  { id: 1, name: 'Notaría 125', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Notaría', active: true },
  { id: 2, name: 'Avalúos Pro', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Avalúos', active: true },
  { id: 3, name: 'Créditos Express', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Financiamiento', active: true },
  { id: 4, name: 'Seguros MX', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Seguros', active: true },
  { id: 5, name: 'Marketing Digital', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Marketing', active: false },
  { id: 6, name: 'Legal Inmobiliario', logo: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=120&h=60&fit=crop', url: '#', category: 'Legal', active: true },
];

const AdminCarruseles = () => {
  const [aliadosLogos, setAliadosLogos] = useState(initialAliadosLogos);
  const [proveedoresLogos, setProveedoresLogos] = useState(initialProveedoresLogos);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState('aliados');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ name: '', logo: '', url: '', category: '' });

  const currentLogos = activeTab === 'aliados' ? aliadosLogos : proveedoresLogos;
  const setCurrentLogos = activeTab === 'aliados' ? setAliadosLogos : setProveedoresLogos;

  const handleToggleActive = (id) => {
    setCurrentLogos(prev => 
      prev.map(item => 
        item.id === id ? { ...item, active: !item.active } : item
      )
    );
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de eliminar este logo?')) {
      setCurrentLogos(prev => prev.filter(item => item.id !== id));
    }
  };

  const handleAddNew = () => {
    if (newItem.name && newItem.logo) {
      const newId = Math.max(...currentLogos.map(l => l.id)) + 1;
      setCurrentLogos(prev => [...prev, { ...newItem, id: newId, active: true }]);
      setNewItem({ name: '', logo: '', url: '', category: '' });
      setShowAddForm(false);
    }
  };

  const handleSaveEdit = () => {
    if (editingItem) {
      setCurrentLogos(prev => 
        prev.map(item => item.id === editingItem.id ? editingItem : item)
      );
      setEditingItem(null);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-carruseles-title">
              Gestión de Carruseles
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Administra los logos que aparecen en el homepage
            </p>
          </div>
          <Button 
            className="bg-gray-900 hover:bg-gray-800 mt-4 sm:mt-0"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Logo
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2">
          <Button
            variant={activeTab === 'aliados' ? 'default' : 'outline'}
            onClick={() => setActiveTab('aliados')}
            className={activeTab === 'aliados' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
            data-testid="tab-aliados"
          >
            Alianzas Estratégicas
            <Badge variant="secondary" className="ml-2 bg-gray-100">
              {aliadosLogos.filter(l => l.active).length}
            </Badge>
          </Button>
          <Button
            variant={activeTab === 'proveedores' ? 'default' : 'outline'}
            onClick={() => setActiveTab('proveedores')}
            className={activeTab === 'proveedores' ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
            data-testid="tab-proveedores"
          >
            Proveedores Certificados
            <Badge variant="secondary" className="ml-2 bg-gray-100">
              {proveedoresLogos.filter(l => l.active).length}
            </Badge>
          </Button>
        </div>

        {/* Add New Form */}
        {showAddForm && (
          <Card className="border border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Agregar Nuevo Logo</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={newItem.name}
                    onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="Nombre de la empresa"
                    className="mt-1"
                    data-testid="input-new-name"
                  />
                </div>
                <div>
                  <Label htmlFor="url">URL del sitio web</Label>
                  <Input
                    id="url"
                    value={newItem.url}
                    onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                    placeholder="https://ejemplo.com"
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="logo">URL del Logo *</Label>
                  <Input
                    id="logo"
                    value={newItem.logo}
                    onChange={(e) => setNewItem({ ...newItem, logo: e.target.value })}
                    placeholder="https://ejemplo.com/logo.png"
                    className="mt-1"
                    data-testid="input-new-logo"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Tamaño recomendado: 120x60px, formatos: PNG, JPG, SVG
                  </p>
                </div>
                {activeTab === 'proveedores' && (
                  <div>
                    <Label htmlFor="category">Categoría</Label>
                    <Input
                      id="category"
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                      placeholder="Ej: Notaría, Avalúos, etc."
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" className="border-gray-300" onClick={() => setShowAddForm(false)}>
                  Cancelar
                </Button>
                <Button 
                  className="bg-gray-900 hover:bg-gray-800"
                  onClick={handleAddNew}
                  data-testid="btn-add-logo"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar Logo
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logos Grid */}
        <Card className="border border-gray-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              {activeTab === 'aliados' ? 'Alianzas Estratégicas' : 'Proveedores Certificados'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {currentLogos.map((item) => (
                <div 
                  key={item.id}
                  className={`
                    flex items-center justify-between p-4 rounded-lg border transition-colors
                    ${item.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'}
                  `}
                  data-testid={`logo-item-${item.id}`}
                >
                  <div className="flex items-center space-x-4">
                    <div className="cursor-grab text-gray-400 hover:text-gray-600">
                      <GripVertical className="h-5 w-5" />
                    </div>
                    <div className="h-12 w-24 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                      <img 
                        src={item.logo} 
                        alt={item.name}
                        className="h-full w-full object-contain"
                      />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      {item.category && (
                        <Badge variant="outline" className="text-xs mt-1">
                          {item.category}
                        </Badge>
                      )}
                      {item.url && item.url !== '#' && (
                        <a 
                          href={item.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          {item.url}
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge 
                      className={item.active 
                        ? 'bg-green-100 text-green-700 hover:bg-green-100' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-100'
                      }
                    >
                      {item.active ? 'Activo' : 'Inactivo'}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(item.id)}
                      className="h-8 w-8 p-0"
                      data-testid={`toggle-${item.id}`}
                    >
                      <CheckCircle2 className={`h-4 w-4 ${item.active ? 'text-green-500' : 'text-gray-400'}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingItem(item)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="h-4 w-4 text-gray-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="h-8 w-8 p-0 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {currentLogos.length === 0 && (
              <div className="text-center py-12">
                <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay logos agregados</p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-gray-300"
                  onClick={() => setShowAddForm(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Agregar el primero
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Modal */}
        {editingItem && (
          <Card className="border border-gray-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Editar Logo</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditingItem(null)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-name">Nombre</Label>
                  <Input
                    id="edit-name"
                    value={editingItem.name}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-url">URL del sitio web</Label>
                  <Input
                    id="edit-url"
                    value={editingItem.url}
                    onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="edit-logo">URL del Logo</Label>
                  <Input
                    id="edit-logo"
                    value={editingItem.logo}
                    onChange={(e) => setEditingItem({ ...editingItem, logo: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button variant="outline" className="border-gray-300" onClick={() => setEditingItem(null)}>
                  Cancelar
                </Button>
                <Button 
                  className="bg-gray-900 hover:bg-gray-800"
                  onClick={handleSaveEdit}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview */}
        <Card className="border border-gray-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Vista Previa del Carrusel</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative overflow-hidden py-4">
              <div className="flex space-x-8 animate-marquee">
                {[...currentLogos.filter(l => l.active), ...currentLogos.filter(l => l.active)].map((logo, index) => (
                  <div 
                    key={`preview-${logo.id}-${index}`} 
                    className="flex-shrink-0 h-16 w-32 bg-gray-50 rounded-lg flex items-center justify-center p-3 grayscale hover:grayscale-0 transition-all border border-gray-100"
                  >
                    <span className="text-xs text-gray-600 font-medium text-center">{logo.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-2">
              Solo los logos activos aparecerán en el carrusel del homepage
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminCarruseles;
